import React, { useState, useEffect } from "react";
import mainlogo from "../../assets/img/customers/logo.png";
import { Box, Tooltip } from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import sidebarLinks from "../../utils/sidebarLinks";
import { useAuth } from "../../auth/workosAuth";
import SidebarSecretsButton from "./SidebarSecretsButton";
import SidebarProfileDropdown from "./SidebarProfileDropdown";
import { useSMSUnread } from "../../contexts/SMSUnreadContext";

const Sidebar = () => {
  const location = useLocation();
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [filteredLinks, setFilteredLinks] = useState(sidebarLinks);
  const { user } = useAuth();
  const { totalUnread } = useSMSUnread();

  // SQL Editor version toggle state
  const [editorVersion, setEditorVersion] = useState(
    localStorage.getItem("sqlEditorVersion") || "v2"
  );

  // Listen for storage changes to sync editor version
  useEffect(() => {
    const handleStorageChange = () => {
      const version = localStorage.getItem("sqlEditorVersion") || "v2";
      setEditorVersion(version);
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  const handleEditorVersionToggle = () => {
    const newVersion = editorVersion === "v1" ? "v2" : "v1";
    setEditorVersion(newVersion);
    localStorage.setItem("sqlEditorVersion", newVersion);
    window.dispatchEvent(new Event("storage"));
    if (window.location.pathname === "/code") {
      window.location.href = "/sql/editor";
    }
  };

  // CRM lives in the CRM app now, so only @datajolt.io users see CRM entries in the
  // legacy sidebar. No per-tenant filtering anymore.
  const filterLinks = (email) => {
    const isDatajoltUser = email?.endsWith("@datajolt.io") || email?.startsWith("maxime.broussard");
    return sidebarLinks.filter((link) => {
      if (isDatajoltUser) return true;
      return !link.href.startsWith("/crm");
    });
  };

  useEffect(() => {
    setFilteredLinks(filterLinks(user?.email));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email]);


  return (
    <Box
      component={"nav"}
      width={60}
      zIndex={1000}
      bgcolor={"#090F47"}
      position={"sticky"}
      top={0}
      left={0}
      height={"100vh"}
    >
      <Link
        href="/home"
        style={{
          paddingBlock: "12px",
          paddingInline: "20px",
          display: "block",
        }}
      >
        <img src={mainlogo} width="85%" alt="Datajolt" />
      </Link>

      <Box position={"relative"} mt={1.5} pb={"100px"}>
        <Box
          component={"span"}
          aria-hidden
          position={"absolute"}
          display={"block"}
          height={30}
          bgcolor={"rgba(255,255,255,0.1)"}
          width={30}
          left={14}
          borderRadius={"5px"}
          top={`${38 * currentPageIndex + 5}px`}
          sx={{
            transition: "all 0.5s ease-in-out",
          }}
        />

        <Box component={"ul"} padding={0} style={{ listStyle: "none" }}>
          {filteredLinks.map((item, i) => {
            // Check if current path matches this link (exact match or starts with for nested routes)
            // /code should highlight the SQL Editor sidebar item
            const isActive = item.href === "/"
              ? location.pathname === "/"
              : item.href === "/crm"
                ? location.pathname.startsWith("/crm") && !location.pathname.startsWith("/crm/inbox")
                : location.pathname.startsWith(item.href) || (item.href === "/sql/editor" && location.pathname === "/code");

            if (isActive && currentPageIndex !== i)
              setCurrentPageIndex(i);

            const isSqlEditor = item.href === "/sql/editor";
            const showVersionPopover = isSqlEditor;

            return (
              <Box component={"li"} key={item.title} position={"relative"}>
                {showVersionPopover ? (
                  // SQL Editor with version toggle - CSS hover approach
                  <Box
                    sx={{
                      position: "relative",
                      "&:hover .version-toggle": {
                        opacity: 1,
                        visibility: "visible",
                        transform: "translateY(-50%) translateX(0)",
                      },
                    }}
                  >
                    <Link
                      to={item.href}
                      style={{
                        color: isActive
                            ? "#e9ecef"
                            : "rgba(233, 236, 239, 0.5)",
                        paddingBlock: "10px",
                        paddingInline: "20px",
                        display: "block",
                      }}
                      aria-label={item.title}
                    >
                      <Box
                        sx={{
                          display: 'inline-block',
                          transition: 'all 0.13s ease-in-out',
                          '&:hover': {
                            color: '#ffffff',
                            transform: 'scale(1.04)',
                          },
                        }}
                      >
                        <item.icon size={18} />
                      </Box>
                    </Link>
                    {/* Version toggle - appears on hover */}
                    <Box
                      className="version-toggle"
                      sx={{
                        position: "absolute",
                        left: "100%",
                        top: "50%",
                        transform: "translateY(-50%) translateX(-10px)",
                        opacity: 0,
                        visibility: "hidden",
                        transition: "all 0.2s ease-in-out",
                        zIndex: 99999,
                        backgroundColor: "#546e7a",
                        borderRadius: "8px",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                        padding: "8px 12px",
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "11px",
                          color: "#fff",
                          fontWeight: "500",
                          marginBottom: "4px",
                        }}
                      >
                        SQL Editor
                      </span>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <span
                          style={{
                            fontSize: "11px",
                            color: editorVersion === "v1" ? "#fff" : "rgba(255,255,255,0.6)",
                            fontWeight: editorVersion === "v1" ? "600" : "400",
                            transition: "all 0.2s",
                          }}
                        >
                          v1
                        </span>
                        <div
                          onClick={(e) => {
                            e.preventDefault();
                            handleEditorVersionToggle();
                          }}
                          style={{
                            width: "32px",
                            height: "18px",
                            backgroundColor: editorVersion === "v2" ? "#3651FE" : "rgba(255,255,255,0.3)",
                            borderRadius: "9px",
                            cursor: "pointer",
                            position: "relative",
                            transition: "background-color 0.2s",
                          }}
                        >
                          <div
                            style={{
                              width: "14px",
                              height: "14px",
                              backgroundColor: "#ffffff",
                              borderRadius: "50%",
                              position: "absolute",
                              top: "2px",
                              left: editorVersion === "v2" ? "16px" : "2px",
                              transition: "left 0.2s",
                              boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                            }}
                          />
                        </div>
                        <span
                          style={{
                            fontSize: "11px",
                            color: editorVersion === "v2" ? "#fff" : "rgba(255,255,255,0.6)",
                            fontWeight: editorVersion === "v2" ? "600" : "400",
                            transition: "all 0.2s",
                          }}
                        >
                          v2
                        </span>
                      </Box>
                    </Box>
                  </Box>
                ) : (
                  // Regular menu item with tooltip
                  <Tooltip
                    title={item.title}
                    placement="right"
                    arrow
                    slotProps={{
                      arrow: { sx: { color: "#546e7a" } },
                      tooltip: {
                        sx: {
                          backgroundColor: "#546e7a",
                          fontSize: "11px",
                          padding: "6px 12px",
                          borderRadius: "6px",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                        },
                      },
                    }}
                  >
                    <Link
                      to={item.href}
                      style={{
                        color: isActive
                            ? "#e9ecef"
                            : "rgba(233, 236, 239, 0.5)",
                        paddingBlock: "10px",
                        paddingInline: "20px",
                        display: "block",
                      }}
                      aria-label={item.title}
                    >
                      <Box
                        sx={{
                          display: 'inline-block',
                          position: 'relative',
                          transition: 'all 0.13s ease-in-out',
                          '&:hover': {
                            color: '#ffffff',
                            transform: 'scale(1.04)',
                          },
                        }}
                      >
                        <item.icon size={18} />
                        {item.href === "/crm/inbox" && totalUnread > 0 && (
                          <Box
                            sx={{
                              position: "absolute",
                              top: -6,
                              right: -10,
                              bgcolor: "#ef4444",
                              color: "#fff",
                              fontSize: "9px",
                              fontWeight: 700,
                              minWidth: 16,
                              height: 16,
                              borderRadius: "8px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              px: 0.4,
                              lineHeight: 1,
                            }}
                          >
                            {totalUnread > 99 ? "99+" : totalUnread}
                          </Box>
                        )}
                      </Box>
                    </Link>
                  </Tooltip>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Bottom Section - Secrets & Profile */}
      <Box
        position="absolute"
        bottom={12}
        left={0}
        right={0}
        display="flex"
        flexDirection="column"
        alignItems="center"
        gap={1.5}
      >
        <SidebarSecretsButton />
        <SidebarProfileDropdown />
      </Box>
    </Box>
  );
};

export default Sidebar;
